import { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  AgxColumn,
  AgxHeader,
  AgxBodyText,
  DocumentTypes,
  DocumentTypesMap,
  AgxRow,
  AgxLabel,
  SPECIAL_CHARACTERS,
  AustralianState,
  CampaignDetailModel,
  MultiOptionButtonOption,
  StakeholderType,
  QldContractStage,
  NswContractStage,
} from '@urbanx/agx-ui-components';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { selectContract } from 'features/campaigns/campaignsReducer';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useSelectedContract } from 'hooks/useSelectedContract';
import { sortMultiOptionButtonOptionsByType } from 'utils/documentUtil';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { formatListOfStrings } from 'utils/formatString';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import FormDetailsHeader from '../../Common/FormDetailsHeader';
import FileList from '../Common/FileList';
import { Tabs } from 'types/Tabs';
import { getEnumValue } from 'helpers/enumHelper';

interface ContractSubmitPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  showDownload?: boolean;
}

const ContractSubmitPanel = ({
  campaign,
  currentTab,
  showDownload = true,
}: ContractSubmitPanelProps) => {
  const dispatch = useAppDispatch();
  const contract = useSelectedContract();
  const { contractStage: stage, lastUpdate, buyers, documents, salePrice } =
    contract || {};
  const {
    address,
    state,
    propertyImageUrls: { medium: imageUrl = null } = {},
  } = campaign;

  if (!stage) throw new Error('Contract stage is undefined');

  const enumType = state === AustralianState.QLD ? QldContractStage : NswContractStage;

  const contractStage = getEnumValue(enumType, stage);

  const headerDetail = `Submitted ${dayjs(lastUpdate).format('DD/MM/YY')}`;

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);

  const getFileLink = useGetFileLink();

  const allBuyerNames = getAllBuyerNames(allBuyers);

  const fileDownloadOptions = useMemo(() => {
    const options: MultiOptionButtonOption[] = [];

    if (!documents || documents.length === 0) return options;

    if (documents.length > 0) {
      documents?.forEach(document => {
        if (document.documentType !== DocumentTypes.SalesContract) {
          options.push({
            text: DocumentTypesMap[document.documentType as keyof typeof DocumentTypesMap],
            documentType: document.documentType,
            onClick: async () => {
              const fileLink = await getFileLink(document.containerFilePath);

              if (!fileLink) return;

              window.open(fileLink);
            },
          });
        }
      });
    }

    const textCount = new Map();
    const uniqueDocumentList = options.map(item => {
      const count = textCount.get(item.text) || 0;
      textCount.set(item.text, count + 1);

      return {
        ...item,
        text: count > 0 ? `${item.text}-${count}` : item.text,
      };
    });

    const finalUniqueDocumentList = [
      ...new Map(uniqueDocumentList.map(item => [item.text, item])).values(),
    ] as MultiOptionButtonOption[];

    return [
      ...(sortMultiOptionButtonOptionsByType(
        finalUniqueDocumentList
      ) as MultiOptionButtonOption[]),
    ];
  }, [documents, getFileLink]);

  const ViewContractButton = () => {
    if (!documents || documents.length === 0 || !showDownload) return <></>;
    return (
      <FileList
        id="viewContractSubmissionPdfs"
        files={
          state === AustralianState.NSW
            ? fileDownloadOptions.filter(
                file => file.text !== DocumentTypesMap.TitleSearch
              )
            : fileDownloadOptions
        }
      />
    );
  };

  return (
    <>
      <FormDetailsHeader
        id={'contract-submit-panel-header'}
        formName="Contract"
        currentTab={currentTab}
        detail={headerDetail}
        onBack={() => dispatch(selectContract(null))}
      />

      <AgxColumn veryLargeGap fill centered>
        <AgxHeader size={2} centered>
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxHeader>
        <AgxBodyText medium centered>
          {formatListOfStrings(allBuyerNames)} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
        </AgxBodyText>
        <AgxRow centered justifyCenter extraClasses={'listing-imageCard'}>
          <div
            style={{
              backgroundImage: `url("${imageUrl || placeHolderImage}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              aspectRatio: '2/1',
            }}
            className={`imageCard-image`}
          >
            <div className="image-tag">
              <AgxLabel medium colour="brand-text-button-color">
                {contractStage}
              </AgxLabel>
            </div>
          </div>
        </AgxRow>
        <ViewContractButton />
      </AgxColumn>
    </>
  );
};

export default ContractSubmitPanel;
